/**
 * MyHistoryPage — Phase 2E agent learning history: completed learning,
 * assessment attempts, earned certificates (with PDF download).
 */
import { useEffect, useState } from 'react';
import { Award, BookOpen, Download, CheckCircle2, XCircle, Timer } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';

export default function MyHistoryPage() {
  const { get } = useApi();
  const [data, setData] = useState(null);

  useEffect(() => {
    get('/me/history').then(setData).catch(() => {});
  }, [get]);

  const downloadCert = async (c) => {
    try {
      const blob = await get(`/me/certificates/${c.certification_id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificate-${c.verify_code}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  if (!data) return <p className="text-sm text-muted-foreground">Loading history…</p>;

  return (
    <div className="space-y-6" data-testid="my-history-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Learning History</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {data.totals.completed_learning} completed · {data.totals.learning_hours} learning hours · {data.totals.certificates} certificate{data.totals.certificates === 1 ? '' : 's'}
        </p>
      </div>

      <Card data-testid="history-certificates">
        <div className="p-3 border-b flex items-center gap-2">
          <Award className="w-4 h-4 text-amber-600" />
          <h4 className="font-display text-sm font-semibold">Certificates</h4>
        </div>
        {data.certificates.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">Complete assigned learning to earn certificates.</p>
        ) : (
          <div className="divide-y">
            {data.certificates.map(c => (
              <div key={c.certification_id} className="p-3 flex items-center justify-between gap-3"
                   data-testid={`cert-${c.certification_id}`}>
                <div>
                  <p className="text-sm font-medium">{c.program_name}</p>
                  <div className="text-xs text-muted-foreground">
                    Issued {new Date(c.issued_at).toLocaleDateString()} · Verify code {c.verify_code}
                    {c.issued_by === 'auto:learning' && <Badge variant="outline" className="ml-2 text-[10px]">Auto</Badge>}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => downloadCert(c)}
                        data-testid={`download-cert-${c.certification_id}`}>
                  <Download className="w-4 h-4 mr-1" /> PDF
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card data-testid="history-learning">
        <div className="p-3 border-b flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-amber-600" />
          <h4 className="font-display text-sm font-semibold">Learning</h4>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Assignment</TableHead><TableHead className="w-20">Items</TableHead>
              <TableHead className="w-24">Quiz</TableHead><TableHead className="w-24">Time</TableHead>
              <TableHead className="w-28">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.learning.map(r => (
              <TableRow key={r.assignment_id}>
                <TableCell className="font-medium text-sm">{r.title}</TableCell>
                <TableCell>{r.progress_items}</TableCell>
                <TableCell>{r.quiz_score != null ? `${r.quiz_score}%` : '—'}</TableCell>
                <TableCell className="text-xs"><Timer className="w-3 h-3 inline mr-1" />{r.time_spent_minutes}m</TableCell>
                <TableCell>
                  {r.completed
                    ? <Badge className="bg-emerald-100 text-emerald-800 border-0">Completed</Badge>
                    : <Badge className="bg-slate-100 text-slate-700 border-0">In Progress</Badge>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card data-testid="history-assessments">
        <div className="p-3 border-b flex items-center gap-2">
          <Award className="w-4 h-4 text-amber-600" />
          <h4 className="font-display text-sm font-semibold">Assessment attempts</h4>
        </div>
        {data.assessments.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">No assessment attempts yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Assessment</TableHead><TableHead className="w-16">Attempt</TableHead>
                <TableHead className="w-20">Score</TableHead><TableHead className="w-20">Result</TableHead>
                <TableHead className="w-36">Submitted</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.assessments.map((r, i) => (
                <TableRow key={i}>
                  <TableCell className="font-medium text-sm">{r.name}</TableCell>
                  <TableCell>#{r.attempt_no}</TableCell>
                  <TableCell>{r.score_pct}%</TableCell>
                  <TableCell>
                    {r.passed
                      ? <span className="inline-flex items-center gap-1 text-emerald-700 text-sm"><CheckCircle2 className="w-4 h-4" /> Pass</span>
                      : <span className="inline-flex items-center gap-1 text-rose-600 text-sm"><XCircle className="w-4 h-4" /> Fail</span>}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{new Date(r.submitted_at).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
